// Reading what a receipt says from its recognised text. Pure functions, no Tesseract, so the browser
// (single upload) and the server (background import) read receipts the same way.
//
// The server uses a generated CommonJS copy: backend/src/services/receipts/parser.generated.js.
// After changing this file run `npm run sync:parser` (a test fails while the copy is stale).
import { CURRENCIES } from './expenseOptions.js';

// What a receipt says about what was bought. Each pattern that appears once adds its weight to a
// category; the clear winner is the answer. Weak words (a place called "Hotel") carry little weight.
const CATEGORY_HINTS = {
    Meals: [
        [/\b(restaurant|cafe|café|caffe|coffee|kaapi|tea|chai|biryani|biriyani|dosa|idli|meals|thali|pizza|burger|bakery|sweets|takeaway|take\s*away|canteen|food|lunch|dinner|breakfast|snacks?|swiggy|zomato|kot|bar|pub)\b/i, 1],
        [/\bfssai\b/i, 2],
        [/\bdine[\s-]*in\b/i, 2],
    ],
    Transportation: [
        [/\b(uber|ola|rapido|taxi|cabs?|ride|metro|fuel|petrol|diesel|parking|toll|fastag)\b/i, 2],
    ],
    Travel: [
        [/\b(flight|airline|airlines|airways|indigo|spicejet|air\s*india|vistara|boarding\s*pass|irctc|railway|railways|train|pnr|redbus|makemytrip|goibibo|cleartrip)\b/i, 2],
    ],
    Accommodation: [
        [/\b(check[\s-]*in|check[\s-]*out|room\s*(charges|rent|tariff)|lodging|oyo|airbnb|resort|guest\s*house|homestay|nights?)\b/i, 2],
        [/\bhotel\b/i, 0.5],
    ],
    'Office Supplies': [
        [/\b(stationery|stationary|printer|cartridge|toner|notebooks?|office\s*supplies|staples)\b/i, 2],
        [/\bpaper\b/i, 1],
    ],
    Communication: [
        [/\b(airtel|jio|vodafone|bsnl|postpaid|prepaid|recharge|broadband|internet|courier|postage)\b/i, 2],
    ],
};

/**
 * The expense category the receipt points to, or '' when nothing clear does (or two categories tie):
 * the person chooses, and there is no default that could be wrong for every receipt.
 */
export const parseCategory = (text) => {
    if (!text) return '';
    const scores = Object.entries(CATEGORY_HINTS).map(([category, hints]) => [category, hints.reduce((sum, [re, weight]) => sum + (re.test(text) ? weight : 0), 0)]);
    scores.sort((x, y) => y[1] - x[1]);
    const [best, runnerUp] = scores;
    return best[1] >= 1 && best[1] > runnerUp[1] ? best[0] : '';
};

/** Everything we can read from the recognised text. Empty strings mean "not found". */
export const parseReceiptText = (text) => ({
    merchant: parseMerchant(text),
    amount: parseAmount(text),
    date: parseDate(text),
    currency: parseCurrency(text),
    category: parseCategory(text),
    rawText: text,
});

// OCR reads 0 as O inside numbers ("1,299.OO", "2O26"). Fix that only in tokens that already contain a digit.
const fixDigits = (text) => text.replace(/[\dOo][\dOo.,/:-]*[\dOo]/g, (tok) => (/\d/.test(tok) ? tok.replace(/[Oo]/g, '0') : tok));

// Parse merchant name from text
export const parseMerchant = (text) => {
    // First line that looks like a name: has letters, is not a heading ("TAX INVOICE"), a field label
    // ("Date: 17/04/25", "Bill No", "GSTIN") or a figure line ("Total 590.00").
    const skip = /^(tax\s*invoice|invoice|cash\s*memo|receipt|bill|welcome|thank|original|duplicate|customer\s*copy|date|time|order|table|token|cashier|gstin|gst|phone|tel|ph|mobile|email|www|http|name|address|dine|fssai|lic)\b/i;
    const figures = /\b(total|amount|qty|price|subtotal|round\s*off)\b/i;
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const name = lines.find(l => (l.match(/[A-Za-z]/g) || []).length >= 3 && !skip.test(l) && !figures.test(l));
    return (name || '').substring(0, 50);
};

// "1,234.56", "1.234,56", "1234", "12,5" -> number. The last separator is the decimal one only when 1-2 digits follow it.
const toNumber = (raw) => {
    const val = raw.replace(/[^\d.,]/g, '');
    const sep = Math.max(val.lastIndexOf('.'), val.lastIndexOf(','));
    const decimals = sep === -1 ? 0 : val.length - sep - 1;
    const cleaned = sep !== -1 && decimals >= 1 && decimals <= 2
        ? `${val.slice(0, sep).replace(/[.,]/g, '')}.${val.slice(sep + 1)}`
        : val.replace(/[.,]/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : NaN;
};

const FIGURE = /[₹$£€]?\s*(\d[\d.,]*)/g;
// A figure that is money paid *to* the shop, as opposed to what the customer handed over or got back.
const PAID_LABEL = /\b(cash|tender(?:ed)?|change|paid|received|tip|gratuity|card|visa|master\s*card|upi|balance\s*due\s*0)\b/i;
const TOTAL_LABEL = /\b(grand\s*total|total\s*(?:amount|due|payable)?|amount\s*(?:due|payable|paid)|net\s*(?:amount|payable)|balance\s*due|to\s*pay|amount)\b/i;
const NOT_TOTAL = /\b(sub\s*-?\s*total|cgst|sgst|igst|vat|tax|gst|discount|items?|qty)\b/i;

// Parse the amount of a receipt: the figure on the "Total" line, not just the biggest number.
export const parseAmount = (rawText) => {
    const text = fixDigits(rawText);
    const lines = text.split('\n');
    const figure = (line) => {
        const found = [...line.matchAll(FIGURE)]
            .map(m => ({ raw: m[1], n: toNumber(m[1]) }))
            // keep real prices: has a decimal part, or is on a total line (whole rupee totals exist)
            .filter(x => !isNaN(x.n));
        return found.length ? found[found.length - 1] : null; // the amount is the last figure on its line
    };

    // 1. Lines labelled as the total (and not a subtotal, tax line or tender line). The last one wins:
    //    grand totals come after subtotals, and a reprinted total is the same figure.
    const labelled = lines
        .filter(l => TOTAL_LABEL.test(l) && !NOT_TOTAL.test(l.replace(/grand\s*total|total\s*(?:amount|due|payable)?/ig, '')) && !PAID_LABEL.test(l))
        .map(figure).filter(Boolean);
    const best = labelled.length ? labelled[labelled.length - 1] : null;
    if (best) return best.n.toFixed(2);

    // 2. No total line (a taxi fare, a till slip cut short): the biggest price, ignoring tender and tax lines.
    const prices = lines
        .filter(l => !PAID_LABEL.test(l) && !NOT_TOTAL.test(l))
        .flatMap(l => [...l.matchAll(/(?<![\d.,])(\d{1,3}(?:[.,]\d{3})*[.,]\d{2}|\d+[.,]\d{2})(?![\d])/g)].map(m => toNumber(m[1])))
        .filter(n => !isNaN(n));
    if (prices.length) return Math.max(...prices).toFixed(2);

    // 3. Only tender/tax lines carried figures: better a guess the person can correct than nothing.
    const any = [...text.matchAll(/(?<![\d.,])(\d{1,3}(?:[.,]\d{3})*[.,]\d{2}|\d+[.,]\d{2})(?![\d])/g)].map(m => toNumber(m[1])).filter(n => !isNaN(n));
    return any.length ? Math.max(...any).toFixed(2) : '';
};

/**
 * Parse the currency from text. An ISO code printed on the receipt always wins; then an unambiguous
 * symbol or word; then Indian tax identifiers. A bare "$" is USD. Returns '' when nothing says,
 * so the form keeps the currency already selected instead of guessing USD.
 */
export const parseCurrency = (text) => {
    const upper = text.toUpperCase();
    const codes = CURRENCIES.filter(c => new RegExp(`(?<![A-Z])${c}(?![A-Z])`).test(upper));
    if (codes.length) {
        // several codes (e.g. a conversion note): the one next to a "total" wins, else the first printed
        const nearTotal = codes.find(c => new RegExp(`TOTAL[^\n]{0,25}(?<![A-Z])${c}(?![A-Z])|(?<![A-Z])${c}(?![A-Z])[^\n]{0,10}TOTAL`).test(upper));
        return nearTotal || codes.sort((a, b) => upper.search(new RegExp(`(?<![A-Z])${a}(?![A-Z])`)) - upper.search(new RegExp(`(?<![A-Z])${b}(?![A-Z])`)))[0];
    }
    if (text.includes('₹') || /\bRS\.?\s*\d/i.test(text) || /\bRUPEES?\b/i.test(text)) return 'INR';
    // OCR often reads the rupee sign as £, $ or %, so Indian tax markers outrank a bare symbol.
    if (/\b(GSTIN|GST|CGST|SGST|IGST|FSSAI|HSN)\b/i.test(text) || /\bTAX\s*INVOICE\b/i.test(text)) return 'INR';
    if (text.includes('€') || /\bEUROS?\b/i.test(text)) return 'EUR';
    if (text.includes('£') || /\bPOUNDS?\b/i.test(text)) return 'GBP';
    if (text.includes('¥') || /\bYEN\b/i.test(text)) return 'JPY';
    if (/\bDIRHAMS?\b/i.test(text)) return 'AED';
    if (text.includes('$') || /\bDOLLARS?\b/i.test(text)) return 'USD';
    return '';
};

// Parse date from text
export const parseDate = (rawText) => {
    const text = fixDigits(rawText);
    // Common date patterns - prioritize DD/MM/YYYY for Indian receipts
    const patterns = [
        // DD/MM/YYYY or DD-MM-YYYY (Indian format)
        { regex: /(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/, format: 'dmy' },
        // DD/MM/YY or DD-MM-YY
        { regex: /(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2})/, format: 'dmy2' },
        // YYYY-MM-DD (ISO format)
        { regex: /(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})/, format: 'ymd' },
        // Month name formats
        { regex: /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,]+(\d{1,2})[\s,]+(\d{4})/i, format: 'mdy' },
        { regex: /(\d{1,2})[\s,]+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,]+(\d{4})/i, format: 'dmy_name' }
    ];

    for (const { regex, format } of patterns) {
        const match = text.match(regex);
        if (match) {
            try {
                let day, month, year;

                if (format === 'dmy') {
                    day = parseInt(match[1]);
                    month = parseInt(match[2]);
                    year = parseInt(match[3]);
                } else if (format === 'dmy2') {
                    day = parseInt(match[1]);
                    month = parseInt(match[2]);
                    year = parseInt(match[3]) + 2000; // Assume 20xx
                } else if (format === 'ymd') {
                    year = parseInt(match[1]);
                    month = parseInt(match[2]);
                    day = parseInt(match[3]);
                } else if (format === 'mdy' || format === 'dmy_name') {
                    // Handle month name formats
                    const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
                    const name = (format === 'mdy' ? match[1] : match[2]).slice(0, 3).toLowerCase();
                    month = MONTHS.indexOf(name) + 1;
                    day = parseInt(format === 'mdy' ? match[2] : match[1]);
                    year = parseInt(match[3]);
                    if (month < 1 || day < 1 || day > 31) continue;
                    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                }

                // Check for common American MM/DD/YYYY if format is DMY
                // e.g. 05/20/2024 - clearly 20 is the day, so 05 is month
                if (day > 12 && month <= 12) {
                    // Already correct as DMY
                } else if (day <= 12 && month > 12) {
                    // This is likely MDY
                    const temp = day;
                    day = month;
                    month = temp;
                }

                // Validate day and month
                if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                    // Create date in YYYY-MM-DD format
                    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const date = new Date(dateStr);
                    if (!isNaN(date.getTime())) {
                        return dateStr;
                    }
                }
            } catch (e) {
                continue;
            }
        }
    }

    // No date found: leave it to the form, which already starts on today.
    return '';
};
